import { useState, useContext } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { FormikTextRediseño } from "../../../components/customFormikComponents/FormikTextRediseño";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../../hooks/userInfoHooks/useUser";
import { useSwal } from "../../../hooks/templateHooks/useSwal";
import { ServeRoute } from "../../../Constants";
import { useCurrentCompany } from "../../../hooks/userInfoHooks/useCurrentCompany";
import { useSpinLoadStore } from "../../../store/useSpinLoadStore";

const ResetPassword = ({ loginState, setLoginState, Token, setIsAnimated }) => {
  const [t] = useTranslation("global");
  const { RequestPasswordChange, UpdatePasswordFromRecover } = useUser();
  const navigate = useNavigate();
  const { Company } = useCurrentCompany();
  const { SuccessError } = useSwal();
  const { ShowLoad, HideLoad } = useSpinLoadStore();
  const [Password, setPassword] = useState({
    password: false,
    confirm: false,
  });
  const [validationMessages, setValidationMessages] = useState({
    minLength: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
  });

  const validationSchema =
    loginState == 1
      ? Yup.object().shape({
          user: Yup.string().trim().required(t("Login.Required")),
        })
      : Yup.object().shape({
          password: Yup.string()
            .trim()
            .required(t("Login.Required"))
            .min(8, "La contraseña debe tener al menos 8 caracteres")
            .matches(/[A-Z]/, "Debe contener al menos una letra mayúscula")
            .matches(/[a-z]/, "Debe contener al menos una letra minúscula")
            .matches(/[0-9]/, "Debe contener al menos un número")
            .matches(
              /[@$!%*?&#<>_/()=¡.,]/,
              "Debe contener al menos un carácter especial"
            ),
          passwordConfirm: Yup.string().trim().required(t("Login.Required")),
        });

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        password: "",
        passwordConfirm: "",
        user: "",
      }}
      isInitialValid={false}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        if (Token) {
          if (values.password.trim() === values.passwordConfirm.trim()) {
            ShowLoad();
            values.token = Token;
            const Result = await UpdatePasswordFromRecover(values);
            if (Result.errorNumber === 0) {
              if (Result.errorMessage === null) {
                SuccessError(
                  "success",
                  t("Login.RecoverPasswordSuccessful"),
                  5000
                );
                HideLoad();
                resetForm();
                navigate(ServeRoute);
                setIsAnimated(false);
              } else {
                SuccessError("warning", t(Result.errorMessage));
                HideLoad();
              }
            } else {
              SuccessError("error", t("Login.RecoverPasswordError"));
              HideLoad();
            }
          } else {
            SuccessError("error", t("Login.PasswordNotCoincide"));
          }
        }
      }}
    >
      {(formik) => (
        <div
          className={`grid grid-flow-row
        flex-col items-center justify-center 
        min-w-[90%] xs:min-w-[90%] md:min-w-[70%] lg:min-w-[70%] xl:min-w-[50%] 2xl:min-w-[40%] 
        rounded-3xl border-[1px] 
        text-center pt-4 
        bg-black/40 backdrop-blur-[3px]
        transition duration-1000 ease-in-out
        relative py-8
        text-white
        `}
        >
          {loginState == 1 ? (
            <>
              <h3 className="text-3xl mt-2 mb-4 font-semibold -mx-4">
                {t("Login.RecoverPassword")}
              </h3>
              <p className="font-semibold">{t("Login.EnterUser")}</p>
            </>
          ) : (
            <>
              <h3 className="text-3xl mt-2 mb-4 font-semibold -mx-4">
                Nueva Contraseña
              </h3>
              <p className="font-semibold">Ingresa tu nueva contraseña</p>
            </>
          )}

          <div
            className={`flex-col justify-center items-center ${
              loginState == 2 ? "w-full" : "w-3/4"
            }  mx-auto`}
          >
            {loginState == 1 ? (
              <>
                <div className="text-center">
                  <FormikTextRediseño
                    name="user"
                    type="text"
                    placeholder={t("Login.User")}
                    icon="i-user"
                  />
                </div>
              </>
            ) : loginState == 2 ? (
              <>
                <div className="col-md-12 mb-2 text-start relative">
                  <FormikTextRediseño
                    name="password"
                    type={Password.password ? "text" : "password"}
                    placeholder={t("Login.Password")}
                    icon="i-lock"
                    onChange={(e) => {
                      const password = e.target.value;
                      formik.setFieldValue("password", password.trim());
                      setValidationMessages({
                        minLength: password.length >= 8,
                        uppercase: /[A-Z]/.test(password),
                        lowercase: /[a-z]/.test(password),
                        number: /[0-9]/.test(password),
                        specialChar: /[@$!%*?&#<>_/()=¡.,]/.test(password),
                      });
                    }}
                  />
                  <button
                    className="absolute top-[50%] left-[82%] z-50 text-black"
                    onClick={(e) => {
                      setPassword({
                        password: !Password.password,
                        confirm: Password.confirm,
                      });
                    }}
                  >
                    <i className="i-eye"></i>
                  </button>
                </div>
                <div className="col-md-12 mb-2 text-start relative">
                  <FormikTextRediseño
                    name="passwordConfirm"
                    type={Password.confirm ? "text" : "password"}
                    placeholder={t("Login.ConfirmPassword")}
                    icon="i-lock"
                    onKeyDown={(e) => {
                      e.code === "Enter" && formik.submitForm();
                    }}
                  />
                  <button
                    className="absolute top-[50%] left-[82%] z-10 text-black"
                    onClick={(e) => {
                      setPassword({
                        password: Password.password,
                        confirm: !Password.confirm,
                      });
                    }}
                  >
                    <i className="i-eye"></i>
                  </button>
                </div>
                <div className="col-md-12 text-start mt-4">
                  <label className="font-bold">Requisitos de contraseña:</label>
                  <ul>
                    <li
                      className={
                        validationMessages.minLength
                          ? "text-[#fcc74c] font-bold"
                          : ""
                      }
                    >
                      <i
                        className={
                          validationMessages.minLength
                            ? "i-ok-circled2"
                            : "i-cancel-circle"
                        }
                      ></i>
                      Mínimo 8 caracteres
                    </li>
                    <li
                      className={
                        validationMessages.uppercase
                          ? "text-[#fcc74c] font-bold"
                          : ""
                      }
                    >
                      <i
                        className={
                          validationMessages.uppercase
                            ? "i-ok-circled2"
                            : "i-cancel-circle"
                        }
                      ></i>
                      Al menos una mayúscula
                    </li>
                    <li
                      className={
                        validationMessages.lowercase
                          ? "text-[#fcc74c] font-bold"
                          : ""
                      }
                    >
                      <i
                        className={
                          validationMessages.lowercase
                            ? "i-ok-circled2"
                            : "i-cancel-circle"
                        }
                      ></i>
                      Al menos una minúscula
                    </li>
                    <li
                      className={
                        validationMessages.number
                          ? "text-[#fcc74c] font-bold"
                          : ""
                      }
                    >
                      <i
                        className={
                          validationMessages.number
                            ? "i-ok-circled2"
                            : "i-cancel-circle"
                        }
                      ></i>
                      Al menos un número
                    </li>
                    <li
                      className={
                        validationMessages.specialChar
                          ? "text-[#F7BF39] font-bold"
                          : ""
                      }
                    >
                      <i
                        className={
                          validationMessages.specialChar
                            ? "i-ok-circled2"
                            : "i-cancel-circle"
                        }
                      ></i>
                      Al menos un carácter especial
                    </li>
                  </ul>
                </div>
              </>
            ) : (
              <></>
            )}

            {loginState == 1 ? (
              <>
                <div
                  onClick={(e) => {
                    formik.handleSubmit();
                  }}
                  className={`btn bg-skin-secondary  w-[80%]  m-4 rounded-xl hover:bg-skin-secondary/50 active:bg-blue-500`}
                >
                  <input
                    type="submit"
                    className="text-white"
                    value={t("Login.Ok")}
                    onClick={async (e) => {
                      if (formik.isValid) {
                        ShowLoad();
                        const Result = await RequestPasswordChange(
                          formik?.values?.user,
                          Company.IcKey
                        );
                        if (!Result.errorMessage) {
                          if (
                            Result.identity === 0 ||
                            Result.identity === null
                          ) {
                            SuccessError(
                              "warning",
                              t("Login.UserNotExistOrDesactivate").replace(
                                "<%Email%>",
                                ""
                              )
                            );
                          } else {
                            SuccessError(
                              "success",
                              t("Login.CheckEmailRecovePassword")
                            );
                            formik.resetForm();
                          }
                        } else {
                          SuccessError("error", t("Login.AnErrorOccurred"));
                        }
                        HideLoad();
                      }
                    }}
                  />
                </div>
                <div
                  className=" absolute left-[1px] top-0 h-full flex items-center rounded-3xl border-none hover:bg-[#ccb089]/10 hover:text-SideBG  overflow-hidden text-[#fff] font-bold text-2xl float-left transition-all ease-in-out duration-300"
                  onClick={(e) => {
                    setPassword({
                      password: !Password.password,
                      confirm: !Password.confirm,
                    });
                    setLoginState(0);
                    navigate(`${ServeRoute}/`);
                  }}
                >
                  <span>
                    <i className="i-left cursor-pointer "></i>
                  </span>
                </div>
              </>
            ) : loginState == 2 ? (
              <>
                <input
                  type="submit"
                  className="btn m-4 text-white bg-skin-primary hover:bg-skin-primary hover:scale-105 transition-transform"
                  value="Restaurar Contraseña"
                  onClick={() => formik.handleSubmit()}
                />
              </>
            ) : (
              <></>
            )}
          </div>
        </div>
      )}
    </Formik>
  );
};

export default ResetPassword;
